import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";


// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A" },
    success: { main: "#059669" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 1060, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "10px 22px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const SecondaryBtn = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  padding: "10px 22px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  border: `1px solid ${alpha("#000", 0.12)}`,
  color: "text.secondary",
  background: "#fff",
  fontFamily: "'Sora', sans-serif",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha("#C4922A", 0.05),
    borderColor: alpha("#C4922A", 0.3),
    color: "#C4922A",
    transform: "translateY(-1px)",
  },
}));

const StatBox = styled(Paper, {
  shouldForwardProp: (p) => p !== "accentcolor" && p !== "delay",
})(({ accentcolor, delay }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(accentcolor || "#2563EB", 0.12)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  background: "#fff",
  flex: 1,
  minWidth: 140,
}));

const StatIconBox = styled(Box)(({ accentcolor }) => ({
  width: 42,
  height: 42,
  borderRadius: 11,
  flexShrink: 0,
  background: alpha(accentcolor || "#2563EB", 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const PromoCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay" && p !== "isTop",
})(({ theme, itemdelay, isTop }) => ({
  borderRadius: 16,
  border: `1.5px solid ${isTop ? alpha("#C4922A", 0.3) : alpha("#000", 0.07)}`,
  boxShadow: isTop
    ? `0 4px 16px ${alpha("#C4922A", 0.12)}, 0 1px 4px rgba(0,0,0,0.04)`
    : "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "20px 22px",
  background: isTop ? alpha("#C4922A", 0.02) : "#fff",
  animation: `${cardEntrance} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: isTop
      ? `0 8px 24px ${alpha("#C4922A", 0.18)}, 0 2px 6px rgba(0,0,0,0.05)`
      : `0 6px 20px rgba(37,99,235,0.08), 0 2px 6px rgba(0,0,0,0.05)`,
    borderColor: isTop ? alpha("#C4922A", 0.4) : alpha("#2563EB", 0.18),
  },
}));

const RankBadge = styled(Box, {
  shouldForwardProp: (p) => p !== "rank",
})(({ rank }) => {
  const colors = {
    1: {
      bg: "linear-gradient(135deg, #C4922A, #F5C842)",
      shadow: "rgba(196,146,42,0.4)",
    },
    2: {
      bg: "linear-gradient(135deg, #6B7280, #9CA3AF)",
      shadow: "rgba(107,114,128,0.3)",
    },
    3: {
      bg: "linear-gradient(135deg, #92400E, #D97706)",
      shadow: "rgba(146,64,14,0.3)",
    },
  };
  const c = colors[rank] || {
    bg: alpha("#2563EB", 0.12),
    shadow: "transparent",
  };
  return {
    width: 36,
    height: 36,
    borderRadius: 10,
    flexShrink: 0,
    background: rank <= 3 ? c.bg : alpha("#2563EB", 0.08),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: rank <= 3 ? `0 3px 8px ${c.shadow}` : "none",
  };
});

const PromoCodePill = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  background: alpha("#2563EB", 0.07),
  border: `1px solid ${alpha("#2563EB", 0.18)}`,
  borderRadius: 8,
  padding: "3px 10px",
  fontFamily: "'Sora', sans-serif",
  fontSize: 12,
  fontWeight: 700,
  color: "#2563EB",
  letterSpacing: "0.3px",
}));

const Spinner = styled(Box)(() => ({
  width: 18,
  height: 18,
  border: "2px solid rgba(255,255,255,0.3)",
  borderTop: "2px solid #fff",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
  display: "inline-block",
  flexShrink: 0,
}));

const SkeletonCard = styled(Box)(() => ({
  borderRadius: 16,
  border: `1px solid ${alpha("#000", 0.06)}`,
  padding: "20px 22px",
  background: "#fff",
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
    }}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminPromoCodePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchPromoCodeData();
  }, []);

  const fetchPromoCodeData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/promo-code-user-count/`);
      setData(res.data.sort((a, b) => b.profit - a.profit));
    } catch (err) {
      console.error("Error fetching promo codes:", err);
    } finally {
      setLoading(false);
    }
  };

  const updatePromoCodes = async () => {
    setUpdating(true);
    try {
      const res = await axios.post(`${API_URL}/admin/update_promo_codes/`);
      if (res.data.status === "success") {
        alert("Promo codes updated successfully");
        fetchPromoCodeData();
      } else {
        alert("Failed to update promo codes");
      }
    } catch {
      alert("An error occurred while updating promo codes");
    } finally {
      setUpdating(false);
    }
  };

  // Stats
  const totalUsers = data.reduce((s, i) => s + (i.user_count || 0), 0);
  const totalProfit = data.reduce((s, i) => s + (i.profit || 0), 0);
  const totalCodes = data.length;

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 28 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                Promo Codes
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Ranked by highest profit
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{ ml: "auto", flexWrap: "wrap", gap: 1 }}
            >
              <SecondaryBtn
                startIcon={
                  <CalendarMonthIcon sx={{ fontSize: "15px !important" }} />
                }
                onClick={() => navigate("/admin-promo-instance")}
              >
                View Dates
              </SecondaryBtn>
              <PrimaryBtn
                startIcon={
                  updating ? (
                    <Spinner />
                  ) : (
                    <RefreshIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                disabled={loading || updating}
                onClick={updatePromoCodes}
              >
                {updating ? "Updating…" : "Update Codes"}
              </PrimaryBtn>
            </Stack>
          </Box>

          {/* ── Stat boxes ── */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <StatBox accentcolor="#2563EB" delay={50}>
              <StatIconBox accentcolor="#2563EB">
                <LocalOfferIcon sx={{ fontSize: 20, color: "#2563EB" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {totalCodes}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Total Codes
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#059669" delay={100}>
              <StatIconBox accentcolor="#059669">
                <PeopleAltIcon sx={{ fontSize: 20, color: "#059669" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {totalUsers}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Total Users
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#C4922A" delay={150}>
              <StatIconBox accentcolor="#C4922A">
                <MonetizationOnIcon sx={{ fontSize: 20, color: "#C4922A" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {totalProfit.toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Total Profit
                </Typography>
              </Box>
            </StatBox>
          </Box>

          {/* ── Promo list card ── */}
          <SectionCard delay={180}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>Leaderboard</CardLabel>
              {!loading && (
                <Chip
                  label={`${data.length} code${data.length !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    background: alpha("#2563EB", 0.08),
                    color: "#2563EB",
                    border: `1px solid ${alpha("#2563EB", 0.18)}`,
                  }}
                />
              )}
            </Box>

            {/* Skeleton loading */}
            {loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {[...Array(5)].map((_, i) => (
                  <SkeletonCard key={i} sx={{ animationDelay: `${i * 90}ms` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <SkeletonBlock w={36} h={36} radius={10} />
                      <SkeletonBlock w={36} h={36} radius="50%" />
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: "7px",
                        }}
                      >
                        <SkeletonBlock w="30%" h={14} />
                        <SkeletonBlock w="20%" h={11} />
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <SkeletonBlock w={70} h={28} radius={8} />
                        <SkeletonBlock w={80} h={28} radius={8} />
                      </Box>
                    </Box>
                  </SkeletonCard>
                ))}
              </Box>
            ) : data.length === 0 ? (
              /* Empty state */
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#C4922A", 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <LocalOfferIcon
                    sx={{ fontSize: 26, color: "#C4922A", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No promo codes found
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Click "Update Codes" to sync promo code data
                </Typography>
              </Box>
            ) : (
              /* Promo cards */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {data.map((item, i) => {
                  const isTop = i === 0;
                  const rank = i + 1;
                  return (
                    <PromoCard
                      key={item.promo_code}
                      itemdelay={i * 55}
                      isTop={isTop}
                    >
                      {/* Gold shimmer strip for #1 */}
                      {isTop && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background:
                              "linear-gradient(90deg, #C4922A, #F5C842, #C4922A)",
                            backgroundSize: "200% 100%",
                            animation: `${shimmer} 2.5s infinite linear`,
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        {/* Rank badge */}
                        <RankBadge rank={rank}>
                          {rank <= 3 ? (
                            <EmojiEventsIcon
                              sx={{
                                fontSize: 18,
                                color:
                                  rank === 1
                                    ? "#fff"
                                    : rank === 2
                                    ? "#fff"
                                    : "#fff",
                              }}
                            />
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#2563EB",
                                fontFamily: "'Sora', sans-serif",
                              }}
                            >
                              {rank}
                            </Typography>
                          )}
                        </RankBadge>

                        {/* User avatar */}
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            flexShrink: 0,
                            background: isTop
                              ? "linear-gradient(135deg, #C4922A, #F5C842)"
                              : `linear-gradient(135deg, ${alpha(
                                  "#2563EB",
                                  0.15
                                )}, ${alpha("#2563EB", 0.28)})`,
                            color: isTop ? "#fff" : "#2563EB",
                            fontSize: 14,
                            fontWeight: 800,
                            fontFamily: "'Sora', sans-serif",
                            boxShadow: isTop
                              ? "0 3px 8px rgba(196,146,42,0.35)"
                              : "none",
                          }}
                        >
                          {item.user?.[0]?.toUpperCase() || "?"}
                        </Avatar>

                        {/* Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                              mb: 0.4,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: "text.primary",
                                fontFamily: "'Sora', sans-serif",
                              }}
                            >
                              {item.user}
                            </Typography>
                            {isTop && (
                              <Chip
                                label="🏆 Top Earner"
                                size="small"
                                sx={{
                                  fontFamily: "'Sora', sans-serif",
                                  fontWeight: 700,
                                  fontSize: 10,
                                  background: alpha("#C4922A", 0.12),
                                  color: "#C4922A",
                                  border: `1px solid ${alpha("#C4922A", 0.25)}`,
                                }}
                              />
                            )}
                          </Box>
                          <PromoCodePill>
                            <LocalOfferIcon sx={{ fontSize: 11 }} />
                            {item.promo_code}
                          </PromoCodePill>
                        </Box>

                        {/* Stats */}
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ flexShrink: 0, flexWrap: "wrap", gap: 0.8 }}
                        >
                          <Tooltip title="Number of users" arrow>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.6,
                                background: alpha("#059669", 0.07),
                                border: `1px solid ${alpha("#059669", 0.18)}`,
                                borderRadius: 2,
                                px: 1.4,
                                py: 0.6,
                              }}
                            >
                              <PeopleAltIcon
                                sx={{ fontSize: 13, color: "#059669" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#059669",
                                  fontFamily: "'Sora', sans-serif",
                                }}
                              >
                                {item.user_count}
                              </Typography>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Total profit" arrow>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.6,
                                background: alpha("#C4922A", 0.08),
                                border: `1px solid ${alpha("#C4922A", 0.2)}`,
                                borderRadius: 2,
                                px: 1.4,
                                py: 0.6,
                              }}
                            >
                              <MonetizationOnIcon
                                sx={{ fontSize: 13, color: "#C4922A" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#C4922A",
                                  fontFamily: "'Sora', sans-serif",
                                }}
                              >
                                {item.profit?.toLocaleString()}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </PromoCard>
                  );
                })}
              </Box>
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminPromoCodePage;
